import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { VoiceChat } from '../components/VoiceChat'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import { GameRoomSidebar } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'
import '../styles/Wavelength.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'

type GameMode = 'select' | 'local' | 'network'
type GamePhase = 'select' | 'setup' | 'lobby' | 'clue' | 'guessing' | 'reveal' | 'gameOver'
type Team = 'red' | 'blue'

interface Spectrum {
  id: number
  left: string
  right: string
}

const SPECTRUMS: Spectrum[] = [
  { id: 1, left: 'Cold', right: 'Hot' },
  { id: 2, left: 'Bad Movie', right: 'Good Movie' },
  { id: 3, left: 'Weak', right: 'Strong' },
  { id: 4, left: 'Boring', right: 'Exciting' },
  { id: 5, left: 'Ugly', right: 'Beautiful' },
  { id: 6, left: 'Sad', right: 'Happy' },
  { id: 7, left: 'Fake', right: 'Real' },
  { id: 8, left: 'Useless', right: 'Useful' },
  { id: 9, left: 'Dumb', right: 'Smart' },
  { id: 10, left: 'Easy', right: 'Hard' },
  { id: 11, left: 'Cheap', right: 'Expensive' },
  { id: 12, left: 'Quiet', right: 'Loud' },
  { id: 13, left: 'Slow', right: 'Fast' },
  { id: 14, left: 'Small', right: 'Large' },
  { id: 15, left: 'Old', right: 'New' },
  { id: 16, left: 'Scary', right: 'Comforting' },
  { id: 17, left: 'Gross', right: 'Delicious' },
  { id: 18, left: 'Silly', right: 'Serious' },
  { id: 19, left: 'Simple', right: 'Complex' },
  { id: 20, left: 'Childish', right: 'Mature' },
  { id: 21, left: 'Gentle', right: 'Aggressive' },
  { id: 22, left: 'Soft', right: 'Hard' },
  { id: 23, left: 'Light', right: 'Heavy' },
  { id: 24, left: 'Dark', right: 'Bright' },
  { id: 25, left: 'Short', right: 'Long' },
  { id: 26, left: 'Wet', right: 'Dry' },
  { id: 27, left: 'Natural', right: 'Artificial' },
  { id: 28, left: 'Healthy', right: 'Unhealthy' },
  { id: 29, left: 'Safe', right: 'Dangerous' },
  { id: 30, left: 'Common', right: 'Rare' },
]

interface PlayerInfo {
  name: string
  team: Team | null
}

export default function Wavelength() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Game mode and phase
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('select')

  // Player info for network mode
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [selectedTeam, setSelectedTeam] = useState<Team>('red')

  // Network multiplayer
  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber,
    playerName,
  })

  const {
    session,
    createSession,
    joinSession,
    voice,
    sendMove,
    onMessage,
  } = enhancedSession

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Handler for sidebar message sending (network mode)
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode !== 'network') return

    // Send chat message
    await sendMove({ type: 'chat', message })

    // Add to local messages
    const newMessage: Message = {
      sender: 'you',
      content: message,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [gameMode, sendMove])

  // Player info for network mode
  const [players, setPlayers] = useState<Map<number, PlayerInfo>>(new Map())

  // Game state
  const [currentTeam, setCurrentTeam] = useState<Team>('red')
  const [redScore, setRedScore] = useState(0)
  const [blueScore, setBlueScore] = useState(0)
  const [currentSpectrum, setCurrentSpectrum] = useState<Spectrum | null>(null)
  const [targetPosition, setTargetPosition] = useState(50) // 0-100
  const [guessPosition, setGuessPosition] = useState(50)
  const [clue, setClue] = useState('')
  const [round, setRound] = useState(1)
  const [maxRounds] = useState(8)

  // Start game (host only in network mode)
  const startGame = () => {
    // In network mode, only host can start
    if (gameMode === 'network' && playerNumber !== 1) return

    setRedScore(0)
    setBlueScore(0)
    setRound(1)
    startNewRound()

    // Send initial state for network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        phase: 'clue',
        redScore: 0,
        blueScore: 0,
        round: 1,
        currentTeam: 'red',
        players: Array.from(players.entries()).map(([id, info]) => ({ id, ...info })),
      }})
    }
  }

  // Start new round (host only in network mode)
  const startNewRound = () => {
    const randomSpectrum = SPECTRUMS[Math.floor(Math.random() * SPECTRUMS.length)]
    const randomTarget = Math.floor(Math.random() * 101) // 0-100
    setCurrentSpectrum(randomSpectrum)
    setTargetPosition(randomTarget)
    setGuessPosition(50)
    setClue('')
    setPhase('clue')

    // Send state for network mode (host generates spectrum/target)
    if (gameMode === 'network' && playerNumber === 1) {
      sendMove({ type: 'game_state', state: {
        spectrum: randomSpectrum,
        targetPosition: randomTarget,
        guessPosition: 50,
        clue: '',
        phase: 'clue',
        currentTeam,
        round,
        redScore,
        blueScore,
      }})
    }
  }

  // Submit clue (clue-giver from current team only)
  const submitClue = () => {
    if (!clue.trim()) return
    setPhase('guessing')

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        clue,
        phase: 'guessing',
        spectrum: currentSpectrum,
        targetPosition,
        guessPosition,
        currentTeam,
        round,
        redScore,
        blueScore,
      }})
    }
  }

  // Submit guess (team members can adjust, anyone can submit)
  const submitGuess = () => {
    setPhase('reveal')
    calculateScore()

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        guessPosition,
        phase: 'reveal',
        spectrum: currentSpectrum,
        targetPosition,
        clue,
        currentTeam,
        round,
        redScore,
        blueScore,
      }})
    }
  }

  // Calculate score (called during submitGuess)
  const calculateScore = () => {
    const distance = Math.abs(guessPosition - targetPosition)
    let points = 0

    // Scoring zones
    if (distance <= 5) points = 4 // Perfect (bullseye)
    else if (distance <= 15) points = 3 // Great
    else if (distance <= 25) points = 2 // Good
    else if (distance <= 35) points = 1 // Close

    const newRedScore = currentTeam === 'red' ? redScore + points : redScore
    const newBlueScore = currentTeam === 'blue' ? blueScore + points : blueScore

    setRedScore(newRedScore)
    setBlueScore(newBlueScore)

    // Update scores in network mode
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        redScore: newRedScore,
        blueScore: newBlueScore,
        phase: 'reveal',
        spectrum: currentSpectrum,
        targetPosition,
        guessPosition,
        clue,
        currentTeam,
        round,
      }})
    }
  }

  // Next round (host only in network mode)
  const nextRound = () => {
    // In network mode, only host can advance
    if (gameMode === 'network' && playerNumber !== 1) return

    if (round >= maxRounds) {
      setPhase('gameOver')

      // Send game over state
      if (gameMode === 'network') {
        sendMove({ type: 'game_state', state: {
          phase: 'gameOver',
          redScore,
          blueScore,
          round,
        }})
      }
      return
    }

    const newRound = round + 1
    const newTeam: Team = currentTeam === 'red' ? 'blue' : 'red'

    setRound(newRound)
    setCurrentTeam(newTeam)
    startNewRound()

    // Send state update
    if (gameMode === 'network') {
      sendMove({ type: 'game_state', state: {
        round: newRound,
        currentTeam: newTeam,
        redScore,
        blueScore,
      }})
    }
  }

  // Update player team in lobby
  const updatePlayerTeam = (team: Team) => {
    setSelectedTeam(team)

    if (gameMode === 'network') {
      const updatedPlayers = new Map(players)
      updatedPlayers.set(playerNumber, { name: playerName, team })
      setPlayers(updatedPlayers)

      // Broadcast player team update
      sendMove({ type: 'player_team', playerId: playerNumber, team })
    }
  }

  // Handle guess position change (sync in network mode)
  const handleGuessChange = (value: number) => {
    setGuessPosition(value)

    // Sync slider position in real-time for network mode
    if (gameMode === 'network') {
      sendMove({ type: 'guess_update', guessPosition: value })
    }
  }

  // Get score zone
  const getScoreZone = (): string => {
    const distance = Math.abs(guessPosition - targetPosition)
    if (distance <= 5) return '🎯 Bullseye! +4 points'
    if (distance <= 15) return '🎉 Great! +3 points'
    if (distance <= 25) return '👍 Good! +2 points'
    if (distance <= 35) return '👌 Close! +1 point'
    return '❌ Miss! +0 points'
  }

  // Sync game state (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribeGameState = onMessage('game_state', (data) => {
      const state = data.state
      if (state.spectrum) setCurrentSpectrum(state.spectrum)
      if (state.targetPosition !== undefined) setTargetPosition(state.targetPosition)
      if (state.guessPosition !== undefined) setGuessPosition(state.guessPosition)
      if (state.clue !== undefined) setClue(state.clue)
      if (state.currentTeam) setCurrentTeam(state.currentTeam)
      if (state.redScore !== undefined) setRedScore(state.redScore)
      if (state.blueScore !== undefined) setBlueScore(state.blueScore)
      if (state.round !== undefined) setRound(state.round)
      if (state.phase) setPhase(state.phase)
      if (state.players) {
        const playersMap = new Map()
        state.players.forEach((p: any) => {
          playersMap.set(p.id, { name: p.name, team: p.team })
        })
        setPlayers(playersMap)
      }
    })

    const unsubscribePlayerTeam = onMessage('player_team', (data) => {
      const updatedPlayers = new Map(players)
      const existingPlayer = updatedPlayers.get(data.playerId) || { name: `Player ${data.playerId}` }
      updatedPlayers.set(data.playerId, {
        ...existingPlayer,
        team: data.team,
      })
      setPlayers(updatedPlayers)
    })

    const unsubscribeGuessUpdate = onMessage('guess_update', (data) => {
      if (data.guessPosition !== undefined) {
        setGuessPosition(data.guessPosition)
      }
    })

    const unsubscribeChat = onMessage('chat', (data) => {
      // Add chat message from opponent
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeGameState()
      unsubscribePlayerTeam()
      unsubscribeGuessUpdate()
      unsubscribeChat()
    }
  }, [gameMode, onMessage, playerNumber])

  // Auto-join if code provided
  useEffect(() => {
    if (autoJoinCode && phase === 'select') {
      setGameMode('network')
      setPhase('setup')
      setPlayerNumber(2)
    }
  }, [autoJoinCode, phase])

  // Check if both players joined (network mode)
  useEffect(() => {
    if (gameMode === 'network' && phase === 'lobby' && session?.player2_name) {
      sounds.playerJoined()
    }
  }, [session?.player2_name, phase, gameMode])

  // Mode selection screen
  if (phase === 'select') {
    return (
      <div className="wavelength-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back
        </button>

        <div className="wavelength-setup">
          <div className="wavelength-logo">📻</div>
          <h1 className="wavelength-title">Wavelength</h1>
          <p className="wavelength-subtitle">
            Guess where your teammate's clue lands on the spectrum
          </p>

          <div className="wavelength-setup-card">
            <h2>Select Game Mode</h2>
            <div className="mode-buttons">
              <button
                className="mode-button mode-local"
                onClick={() => { setGameMode('local'); setPhase('setup'); }}
              >
                <div className="mode-icon">👥</div>
                <h3>Local Game</h3>
                <p>Play with friends on this device</p>
              </button>

              <button
                className="mode-button mode-network"
                onClick={() => { setGameMode('network'); setPhase('setup'); }}
              >
                <div className="mode-icon">🌐</div>
                <h3>Online</h3>
                <p>Play with anyone, anywhere (2 players)</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Setup phase
  if (phase === 'setup') {
    // Network mode setup
    if (gameMode === 'network') {
      const handleCreateSession = async () => {
        if (!playerName.trim()) {
          alert('Please enter your name')
          return
        }
        UserProfileManager.save(playerName)
        const session = await createSession('wavelength', playerName)
        if (session) {
          setPlayerNumber(1)
          const newPlayers = new Map()
          newPlayers.set(1, { name: playerName, team: null })
          setPlayers(newPlayers)
          setPhase('lobby')
          sounds.success()
        }
      }

      const handleJoinSession = async () => {
        const code = autoJoinCode || prompt('Enter game code:')
        if (!code) return
        if (!playerName.trim()) {
          alert('Please enter your name')
          return
        }
        UserProfileManager.save(playerName)
        const success = await joinSession(code, playerName)
        if (success) {
          setPlayerNumber(2)
          const newPlayers = new Map()
          newPlayers.set(2, { name: playerName, team: null })
          setPlayers(newPlayers)
          setPhase('lobby')
          sounds.success()
        }
      }

      return (
        <div className="wavelength-container">
          <button onClick={() => setPhase('select')} className="back-button">
            ← Back
          </button>

          <div className="wavelength-setup">
            <div className="wavelength-logo">📻</div>
            <h1 className="wavelength-title">Wavelength</h1>
            <p className="wavelength-subtitle">Online Multiplayer</p>

            <div className="wavelength-setup-card">
              <div className="network-setup">
                <label>Your Name:</label>
                <input
                  type="text"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Enter your name"
                  className="name-input"
                />

                <div className="network-buttons">
                  <button onClick={handleCreateSession} className="create-button">
                    Create Game
                  </button>
                  <button onClick={handleJoinSession} className="join-button">
                    Join Game
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    // Local mode setup
    return (
      <div className="wavelength-container">
        <button onClick={() => setPhase('select')} className="back-button">
          ← Back
        </button>

        <div className="wavelength-setup">
          <div className="wavelength-logo">📻</div>
          <h1 className="wavelength-title">Wavelength</h1>
          <p className="wavelength-subtitle">
            Guess where your teammate's clue lands on the spectrum
          </p>

          <div className="wavelength-setup-card">
            <h2>How to Play</h2>
            <div className="rules-list">
              <div className="rule-item">
                <span className="rule-icon">👥</span>
                <span>2 teams take turns giving clues and guessing</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">📊</span>
                <span>Each round has a spectrum (e.g., "Cold" ←→ "Hot")</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🎯</span>
                <span>A secret target position is randomly chosen</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">💬</span>
                <span>The clue-giver sees the target and gives a one-word clue</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🤔</span>
                <span>Your team guesses where on the spectrum the clue points</span>
              </div>
              <div className="rule-item">
                <span className="rule-icon">🏆</span>
                <span>Closer guesses = more points! (4/3/2/1/0)</span>
              </div>
            </div>

            <div className="scoring-guide">
              <h3>Scoring Zones:</h3>
              <div className="zone-list">
                <div className="zone-item bullseye">🎯 ±5 = 4 points</div>
                <div className="zone-item great">🎉 ±15 = 3 points</div>
                <div className="zone-item good">👍 ±25 = 2 points</div>
                <div className="zone-item close">👌 ±35 = 1 point</div>
                <div className="zone-item miss">❌ Beyond ±35 = 0 points</div>
              </div>
            </div>

            <button onClick={startGame} className="start-button">
              Start Game ({maxRounds} Rounds)
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Lobby phase (network mode)
  if (phase === 'lobby') {
    const shareUrl = `${window.location.origin}${window.location.pathname}?join=${session?.session_code}`
    const copyShareLink = () => {
      navigator.clipboard.writeText(shareUrl)
      alert('Link copied to clipboard!')
    }

    // Check if we have enough players and all have selected teams
    const allPlayersReady = Array.from(players.values()).every(p => p.team !== null)
    const minPlayers = players.size >= 2 // Need at least 2 players

    return (
      <div className="wavelength-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back
        </button>

        <div className="wavelength-setup">
          <div className="wavelength-logo">📻</div>
          <h1 className="wavelength-title">Game Lobby</h1>

          <div className="wavelength-setup-card">
            <div className="lobby-content">
              <div className="session-code">
                <label>Game Code:</label>
                <div className="code-display">{session?.session_code}</div>
                <button onClick={copyShareLink} className="copy-button">
                  Copy Share Link
                </button>
              </div>

              <div className="team-selector">
                <label>Select Your Team:</label>
                <div className="team-buttons">
                  <button
                    className={`team-btn red-btn ${selectedTeam === 'red' ? 'selected' : ''}`}
                    onClick={() => updatePlayerTeam('red')}
                  >
                    🔴 Red Team
                  </button>
                  <button
                    className={`team-btn blue-btn ${selectedTeam === 'blue' ? 'selected' : ''}`}
                    onClick={() => updatePlayerTeam('blue')}
                  >
                    🔵 Blue Team
                  </button>
                </div>
              </div>

              <div className="players-list">
                <h3>Players ({players.size}/2):</h3>
                {Array.from(players.entries()).map(([id, info]) => (
                  <div key={id} className="player-item">
                    <span className="player-status">{info.team ? '✓' : '⏳'}</span>
                    <span>{info.name}</span>
                    {info.team && (
                      <span className={`player-team ${info.team}`}>
                        {info.team === 'red' ? '🔴 Red' : '🔵 Blue'}
                      </span>
                    )}
                  </div>
                ))}
                {players.size < 2 && (
                  <div className="waiting-message">
                    Waiting for {2 - players.size} more player{2 - players.size !== 1 ? 's' : ''}...
                  </div>
                )}
              </div>

              <VoiceChat
                connectionStatus={voice.connectionStatus}
                voiceStatus={{
                  localMuted: voice.isMuted,
                  remoteMuted: false,
                  localConnected: voice.isConnected,
                  remoteConnected: voice.isConnected
                }}
                playerName={playerName}
                opponentName={playerNumber === 1 ? session?.player2_name : session?.player1_name}
                onConnect={voice.connect}
                onToggleMute={voice.toggleMute}
              />

              {allPlayersReady && minPlayers && playerNumber === 1 && (
                <button onClick={startGame} className="start-button">
                  Start Game
                </button>
              )}

              {allPlayersReady && minPlayers && playerNumber !== 1 && (
                <div className="waiting-message">
                  Waiting for host to start the game...
                </div>
              )}

              {(!allPlayersReady || !minPlayers) && (
                <div className="waiting-message">
                  {!minPlayers ? 'Need at least 2 players to start' : 'Waiting for all players to select their team...'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Restart game
  const restartGame = () => {
    if (gameMode === 'network') {
      setPhase('lobby')
    } else {
      setPhase('setup')
    }
  }

  if (phase === 'gameOver') {
    const winner = redScore > blueScore ? 'red' : blueScore > redScore ? 'blue' : 'tie'
    return (
      <div className="wavelength-container">
        <div className="gameover-screen">
          <div className="gameover-icon">
            {winner === 'tie' ? '🤝' : winner === 'red' ? '🔴' : '🔵'}
          </div>
          <h1>Game Over!</h1>
          {winner === 'tie' ? (
            <p className="gameover-message">It's a tie! You're on the same wavelength! 🎵</p>
          ) : (
            <p className="gameover-message">
              {winner === 'red' ? '🔴 Red Team' : '🔵 Blue Team'} Wins!
            </p>
          )}

          <div className="final-scores">
            <div className="final-score red">
              <span className="score-label">Red Team</span>
              <span className="score-value">{redScore}</span>
            </div>
            <div className="final-score blue">
              <span className="score-label">Blue Team</span>
              <span className="score-value">{blueScore}</span>
            </div>
          </div>

          <div className="gameover-buttons">
            <button onClick={restartGame} className="restart-button">
              Play Again
            </button>
            <button onClick={() => navigate('/')} className="home-button">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Playing phases
  return (
    <div className="wavelength-container universal-game-room">
      <div className="wavelength-header">
        <button onClick={restartGame} className="back-button">
          ← New Game
        </button>

        {gameMode === 'network' && (
          <div className="voice-chat-container">
            <VoiceChat
              connectionStatus={voice.connectionStatus}
              voiceStatus={{
                localMuted: voice.isMuted,
                remoteMuted: false,
                localConnected: voice.isConnected,
                remoteConnected: voice.isConnected
              }}
              playerName={playerName}
              opponentName={playerNumber === 1 ? session?.player2_name : session?.player1_name}
              onConnect={voice.connect}
              onToggleMute={voice.toggleMute}
            />
          </div>
        )}

        <div className="game-stats">
          <div className="round-indicator">
            Round {round}/{maxRounds}
          </div>
          <div className="score-display">
            <div className={`team-score red ${currentTeam === 'red' ? 'active' : ''}`}>
              🔴 {redScore}
            </div>
            <div className={`team-score blue ${currentTeam === 'blue' ? 'active' : ''}`}>
              🔵 {blueScore}
            </div>
          </div>
        </div>
      </div>

      <div className="turn-banner">
        {currentTeam === 'red' ? '🔴 Red Team' : '🔵 Blue Team'}'s Turn
        {gameMode === 'network' && selectedTeam && (
          <span className="your-team-indicator">
            {' '}(You are {selectedTeam === 'red' ? '🔴 Red' : '🔵 Blue'})
          </span>
        )}
      </div>

      <div className="game-room-content">
        {/* Game Board Area */}
        <div className="game-board-area">

      {/* Clue Phase */}
      {phase === 'clue' && currentSpectrum && (
        <div className="clue-phase">
          {/* Clue-giver view (only for current team in network mode, or everyone in local mode) */}
          {(gameMode === 'local' || (gameMode === 'network' && selectedTeam === currentTeam)) ? (
            <>
              <h2 className="phase-title">🎭 Clue-Giver: Give Your Clue</h2>

              <div className="spectrum-display">
                <div className="spectrum-label left">{currentSpectrum.left}</div>
                <div className="spectrum-bar">
                  <div
                    className="target-marker clue-giver-view"
                    style={{ left: `${targetPosition}%` }}
                  >
                    🎯
                  </div>
                </div>
                <div className="spectrum-label right">{currentSpectrum.right}</div>
              </div>

              <div className="target-info">
                Target Position: <strong>{targetPosition}%</strong>
                <br />
                <span className="hint-text">
                  {targetPosition < 25 && `Very close to "${currentSpectrum.left}"`}
                  {targetPosition >= 25 && targetPosition < 45 && `Leaning toward "${currentSpectrum.left}"`}
                  {targetPosition >= 45 && targetPosition <= 55 && 'Right in the middle'}
                  {targetPosition > 55 && targetPosition <= 75 && `Leaning toward "${currentSpectrum.right}"`}
                  {targetPosition > 75 && `Very close to "${currentSpectrum.right}"`}
                </span>
              </div>

              <div className="clue-input-section">
                <label>Your One-Word Clue:</label>
                <input
                  type="text"
                  value={clue}
                  onChange={(e) => setClue(e.target.value)}
                  placeholder="Enter a clue..."
                  className="clue-input"
                  maxLength={30}
                />
                <button
                  onClick={submitClue}
                  disabled={!clue.trim()}
                  className="submit-clue-button"
                >
                  Submit Clue
                </button>
              </div>
            </>
          ) : (
            /* Other team waits */
            <>
              <h2 className="phase-title">⏳ Waiting for {currentTeam === 'red' ? '🔴 Red Team' : '🔵 Blue Team'} Clue-Giver</h2>
              <div className="spectrum-display">
                <div className="spectrum-label left">{currentSpectrum.left}</div>
                <div className="spectrum-bar"></div>
                <div className="spectrum-label right">{currentSpectrum.right}</div>
              </div>
              <div className="waiting-message">
                The clue-giver is thinking of a clue...
              </div>
            </>
          )}
        </div>
      )}

      {/* Guessing Phase */}
      {phase === 'guessing' && currentSpectrum && (
        <div className="guessing-phase">
          {(gameMode === 'local' || (gameMode === 'network' && selectedTeam === currentTeam)) ? (
            <>
              <h2 className="phase-title">🤔 Team: Make Your Guess!</h2>

              <div className="clue-display">
                The clue is: <strong>"{clue}"</strong>
              </div>

              <div className="spectrum-display">
                <div className="spectrum-label left">{currentSpectrum.left}</div>
                <div className="spectrum-bar">
                  <div
                    className="guess-marker"
                    style={{ left: `${guessPosition}%` }}
                  >
                    📍
                  </div>
                </div>
                <div className="spectrum-label right">{currentSpectrum.right}</div>
              </div>

              <div className="guess-slider-section">
                <label>Drag to guess where "{clue}" lands on the spectrum:</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={guessPosition}
                  onChange={(e) => handleGuessChange(Number(e.target.value))}
                  className="guess-slider"
                />
                <div className="guess-value">{guessPosition}%</div>
              </div>

              <button onClick={submitGuess} className="submit-guess-button">
                Lock In Guess
              </button>
            </>
          ) : (
            /* Other team watches */
            <>
              <h2 className="phase-title">👀 Watching {currentTeam === 'red' ? '🔴 Red Team' : '🔵 Blue Team'} Guess</h2>

              <div className="clue-display">
                The clue is: <strong>"{clue}"</strong>
              </div>

              <div className="spectrum-display">
                <div className="spectrum-label left">{currentSpectrum.left}</div>
                <div className="spectrum-bar">
                  <div
                    className="guess-marker"
                    style={{ left: `${guessPosition}%` }}
                  >
                    📍
                  </div>
                </div>
                <div className="spectrum-label right">{currentSpectrum.right}</div>
              </div>

              <div className="guess-value">{guessPosition}%</div>

              <div className="waiting-message">
                {currentTeam === 'red' ? '🔴 Red Team' : '🔵 Blue Team'} is making their guess...
              </div>
            </>
          )}
        </div>
      )}

      {/* Reveal Phase */}
      {phase === 'reveal' && currentSpectrum && (
        <div className="reveal-phase">
          <h2 className="phase-title">🎯 Reveal!</h2>

          <div className="score-result">{getScoreZone()}</div>

          <div className="spectrum-display">
            <div className="spectrum-label left">{currentSpectrum.left}</div>
            <div className="spectrum-bar">
              <div
                className="target-marker"
                style={{ left: `${targetPosition}%` }}
              >
                🎯
              </div>
              <div
                className="guess-marker"
                style={{ left: `${guessPosition}%` }}
              >
                📍
              </div>
            </div>
            <div className="spectrum-label right">{currentSpectrum.right}</div>
          </div>

          <div className="reveal-stats">
            <div className="stat">
              <span className="stat-label">Target:</span>
              <span className="stat-value">{targetPosition}%</span>
            </div>
            <div className="stat">
              <span className="stat-label">Your Guess:</span>
              <span className="stat-value">{guessPosition}%</span>
            </div>
            <div className="stat">
              <span className="stat-label">Distance:</span>
              <span className="stat-value">{Math.abs(guessPosition - targetPosition)}%</span>
            </div>
          </div>

          {(gameMode === 'local' || playerNumber === 1) ? (
            <button onClick={nextRound} className="next-round-button">
              {round >= maxRounds ? 'See Final Scores' : 'Next Round'}
            </button>
          ) : (
            <div className="waiting-message">
              Waiting for host to continue...
            </div>
          )}
        </div>
      )}

        </div>
        {/* End Game Board Area */}

        {/* Universal Sidebar for network mode */}
        {gameMode === 'network' && (
          <GameRoomSidebar
            mode="network"
            gameState={{
              turn: currentTeam === 'red' ? '🔴 Red Team' : '🔵 Blue Team',
              phase: phase.charAt(0).toUpperCase() + phase.slice(1),
              score: { Red: redScore, Blue: blueScore }
            }}
            messages={chatMessages}
            historyItems={historyItems}
            onSendMessage={handleSidebarMessage}
            collapsed={sidebar.collapsed}
            onToggleCollapse={sidebar.toggle}
            position={sidebar.position}
            quickReactions={true}
          />
        )}
      </div>
      {/* End game-room-content */}
    </div>
  )
}
